import { Language } from "@linqx/i18n";
import { LegalLayout } from "@/components/sections/legal-layout";

export function EulaContent() {
  return (
    <LegalLayout
      title={Language.getKey("landing_legal_eula_title")}
      lastUpdated={Language.getKey("landing_legal_eula_updated")}
    >
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          Table of Contents
        </h2>
        <ol className="list-decimal list-inside text-muted-foreground space-y-1">
          <li>
            <a href="#section-1" className="text-primary hover:underline">
              Introduction
            </a>
          </li>
          <li>
            <a href="#section-2" className="text-primary hover:underline">
              Acceptance of Terms
            </a>
          </li>
          <li>
            <a href="#section-3" className="text-primary hover:underline">
              Definitions
            </a>
          </li>
          <li>
            <a href="#section-4" className="text-primary hover:underline">
              Use of the Services
            </a>
          </li>
          <li>
            <a href="#section-5" className="text-primary hover:underline">
              Intellectual Property Rights
            </a>
          </li>
          <li>
            <a href="#section-6" className="text-primary hover:underline">
              Privacy
            </a>
          </li>
          <li>
            <a href="#section-7" className="text-primary hover:underline">
              Disclaimer of Warranties
            </a>
          </li>
          <li>
            <a href="#section-8" className="text-primary hover:underline">
              Limitation of Liability
            </a>
          </li>
          <li>
            <a href="#section-9" className="text-primary hover:underline">
              Indemnification
            </a>
          </li>
          <li>
            <a href="#section-10" className="text-primary hover:underline">
              Termination
            </a>
          </li>
          <li>
            <a href="#section-11" className="text-primary hover:underline">
              Governing Law and Dispute Resolution
            </a>
          </li>
          <li>
            <a href="#section-12" className="text-primary hover:underline">
              Contact Information
            </a>
          </li>
          <li>
            <a href="#section-13" className="text-primary hover:underline">
              Changes to This EULA
            </a>
          </li>
          <li>
            <a href="#section-14" className="text-primary hover:underline">
              Entire Agreement
            </a>
          </li>
          <li>
            <a href="#section-15" className="text-primary hover:underline">
              Severability
            </a>
          </li>
          <li>
            <a href="#section-16" className="text-primary hover:underline">
              Jurisdiction-Specific Provisions
            </a>
          </li>
          <li>
            <a href="#acknowledgment" className="text-primary hover:underline">
              Acknowledgment
            </a>
          </li>
        </ol>
      </section>

      <section id="section-1" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          1. Introduction
        </h2>
        <p className="text-muted-foreground">
          This End User License Agreement (&ldquo;EULA&rdquo; or
          &ldquo;Agreement&rdquo;) is a binding contract between you
          (&ldquo;User&rdquo; or &ldquo;you&rdquo;) and Linqx.
          (&ldquo;Linqx,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
          &ldquo;our&rdquo;), a company incorporated in California, USA, and the
          owner and operator of the website located at https://getlinqx.com (the
          &ldquo;Website&rdquo;) and the Linqx mobile application (the
          &ldquo;App&rdquo;). This EULA governs your use of the Website, App,
          and all related content, features, and services (collectively, the
          &ldquo;Services&rdquo;), including tools that connect Users for
          immediate, in-person shared experiences.
        </p>
      </section>

      <section id="section-2" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          2. Acceptance of Terms
        </h2>
        <p className="text-muted-foreground">
          By accessing or using the Services, including creating an account or
          clicking &ldquo;I Agree&rdquo; during registration, you confirm that
          you have read, understood, and agree to be bound by this EULA and our{" "}
          <a href="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </a>
          . If you do not agree, you must not use the Services.
        </p>
      </section>

      <section id="section-3" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          3. Definitions
        </h2>
        <ul className="list-disc list-inside text-muted-foreground space-y-2">
          <li>
            <strong>&ldquo;Content&rdquo;</strong>: Any text, images, audio,
            video, or other materials posted or uploaded to the Services by
            Linqx or Users.
          </li>
          <li>
            <strong>&ldquo;Intellectual Property Rights&rdquo;</strong>: All
            patents, trademarks, copyrights, trade secrets, and other
            intellectual property rights recognized by law.
          </li>
          <li>
            <strong>&ldquo;Services&rdquo;</strong>: All features, tools, and
            services provided through the Website and App, including but not
            limited to social networking, messaging, content sharing, and
            facilitating real-time connections between Users for shared
            in-person experiences.
          </li>
          <li>
            <strong>&ldquo;User-Generated Content&rdquo;</strong>: Any Content
            posted or uploaded to the Services by a User.
          </li>
          <li>
            <strong>&ldquo;Website&rdquo;</strong>: The website at
            https://getlinqx.com and all associated subdomains.
          </li>
          <li>
            <strong>&ldquo;App&rdquo;</strong>: The Linqx mobile application
            available on supported platforms.
          </li>
        </ul>
      </section>

      <section id="section-4" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          4. Use of the Services
        </h2>

        <h3 className="text-xl font-medium mb-3 text-foreground">
          4.1 Eligibility
        </h3>
        <p className="text-muted-foreground mb-6">
          You must be at least 18 years old to use the Services. By using the
          Services, you represent and warrant that you are 18 or older and have
          the legal capacity to enter into this Agreement.
        </p>

        <h3 className="text-xl font-medium mb-3 text-foreground">
          4.2 Account Registration
        </h3>
        <p className="text-muted-foreground mb-6">
          To access certain features, you must register for an account with
          accurate and complete information. You agree to update your account
          details as needed. Providing false information may result in account
          termination.
        </p>

        <h3 className="text-xl font-medium mb-3 text-foreground">
          4.3 Password Security
        </h3>
        <p className="text-muted-foreground mb-6">
          You are responsible for keeping your account password confidential and
          for all activities under your account. Notify us immediately at{" "}
          <a
            href="mailto:support@getlinqx.com"
            className="text-primary hover:underline"
          >
            support@getlinqx.com
          </a>{" "}
          if you suspect unauthorized use.
        </p>

        <h3 className="text-xl font-medium mb-3 text-foreground">
          4.4 Prohibited Conduct
        </h3>
        <p className="text-muted-foreground mb-4">You agree not to:</p>
        <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
          <li>Use the Services for any illegal or unauthorized purpose.</li>
          <li>
            Post or upload Content that is obscene, defamatory, threatening, or
            infringes on others&apos; rights.
          </li>
          <li>
            Harass, intimidate, or harm other Users through the Services or
            during shared experiences.
          </li>
          <li>
            Misrepresent your identity, intentions, or availability for shared
            experiences.
          </li>
          <li>Send spam or unsolicited messages.</li>
          <li>
            Collect or store personal data about other Users without their
            consent.
          </li>
          <li>
            Engage in unsafe, harmful, or illegal behavior during interactions
            facilitated by the Services.
          </li>
        </ul>

        <h3 className="text-xl font-medium mb-3 text-foreground">
          4.5 User Safety
        </h3>
        <p className="text-muted-foreground">
          You are solely responsible for your safety during shared experiences
          facilitated by the Services. Linqx does not vet, monitor, or guarantee
          the behavior, identity, or suitability of other Users. Use caution and
          judgment when meeting others.
        </p>
      </section>

      <section id="section-5" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          5. Intellectual Property Rights
        </h2>

        <h3 className="text-xl font-medium mb-3 text-foreground">
          5.1 Linqx Intellectual Property
        </h3>
        <p className="text-muted-foreground mb-6">
          The Services, including all Content and Intellectual Property Rights
          therein (except User-Generated Content), are owned by Linqx or its
          licensors. You may not reproduce, modify, or distribute our Content
          without prior written consent.
        </p>

        <h3 className="text-xl font-medium mb-3 text-foreground">
          5.2 User-Generated Content
        </h3>
        <p className="text-muted-foreground">
          You retain ownership of your User-Generated Content. By posting or
          uploading it, you grant Linqx a non-exclusive, royalty-free, worldwide
          license to use, reproduce, modify, and display it solely to operate,
          promote, and improve the Services. This license ends when you delete
          your Content or account, except for data we must retain by law.
        </p>
      </section>

      <section id="section-6" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          6. Privacy
        </h2>
        <p className="text-muted-foreground">
          Your use of the Services is subject to our{" "}
          <a href="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </a>{" "}
          which explains how we collect, use, and protect your personal
          information, including location data used to facilitate shared
          experiences.
        </p>
      </section>

      <section id="section-7" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          7. Disclaimer of Warranties
        </h2>
        <p className="text-muted-foreground">
          The Services are provided &ldquo;as is&rdquo; and &ldquo;as
          available.&rdquo; Linqx disclaims all warranties, express or implied,
          including merchantability, fitness for a particular purpose, and
          non-infringement. We do not guarantee the accuracy, safety, or outcome
          of User interactions or shared experiences.
        </p>
      </section>

      <section id="section-8" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          8. Limitation of Liability
        </h2>
        <p className="text-muted-foreground">
          To the fullest extent permitted by law, Linqx, its officers,
          directors, employees, and agents will not be liable for any indirect,
          incidental, consequential, or punitive damages arising from your use
          of the Services, including damages from interactions with other Users
          (e.g., physical, emotional, or financial harm). Our total liability,
          if any, will not exceed $100.
        </p>
      </section>

      <section id="section-9" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          9. Indemnification
        </h2>
        <p className="text-muted-foreground">
          You agree to indemnify and hold harmless Linqx, its officers,
          directors, employees, and agents from any claims, damages, losses, or
          expenses (including reasonable attorneys&apos; fees) arising from your
          use of the Services, your User-Generated Content, or your violation of
          this EULA.
        </p>
      </section>

      <section id="section-10" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          10. Termination
        </h2>
        <p className="text-muted-foreground mb-4">
          We may suspend or terminate your access to the Services at our
          discretion, with or without notice, including for violations of this
          EULA. You may terminate your account at any time by contacting{" "}
          <a
            href="mailto:support@getlinqx.com"
            className="text-primary hover:underline"
          >
            support@getlinqx.com
          </a>
          . Sections 5, 7, 8, 9, 10, and 13 survive termination.
        </p>
      </section>

      <section id="section-11" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          11. Governing Law and Dispute Resolution
        </h2>
        <p className="text-muted-foreground">
          This EULA is governed by the laws of the State of California, USA,
          without regard to conflict of law principles. Any disputes will be
          resolved through binding arbitration in San Francisco, California,
          under the rules of the American Arbitration Association (AAA), except
          that either party may seek injunctive relief in court for intellectual
          property violations.
        </p>
      </section>

      <section id="section-12" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          12. Contact Information
        </h2>
        <p className="text-muted-foreground mb-4">
          For questions or concerns about this EULA, contact us at:
        </p>
        <ul className="list-disc list-inside text-muted-foreground space-y-2">
          <li>
            <strong>Email</strong>:{" "}
            <a
              href="mailto:support@getlinqx.com"
              className="text-primary hover:underline"
            >
              support@getlinqx.com
            </a>
          </li>
          <li>
            <strong>Mail</strong>: 5432 Pioneer Blvd, Whittier CA 90640
          </li>
        </ul>
      </section>

      <section id="section-13" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          13. Changes to This EULA
        </h2>
        <p className="text-muted-foreground">
          We may update this EULA at any time by posting the revised version on
          the Website or App. Your continued use of the Services after changes
          constitutes acceptance of the new terms.
        </p>
      </section>

      <section id="section-14" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          14. Entire Agreement
        </h2>
        <p className="text-muted-foreground">
          This EULA, together with the Privacy Policy, is the entire agreement
          between you and Linqx regarding the Services, superseding all prior
          agreements.
        </p>
      </section>

      <section id="section-15" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          15. Severability
        </h2>
        <p className="text-muted-foreground">
          If any provision of this EULA is found invalid or unenforceable, it
          will be severed, and the remaining provisions will remain in effect.
        </p>
      </section>

      <section id="section-16" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          16. Jurisdiction-Specific Provisions
        </h2>
        <ul className="list-disc list-inside text-muted-foreground space-y-2">
          <li>
            <strong>European Union</strong>: If you reside in the EU, you may
            withdraw from this EULA within 14 days of acceptance by emailing{" "}
            <a
              href="mailto:support@getlinqx.com"
              className="text-primary hover:underline"
            >
              support@getlinqx.com
            </a>{" "}
            with your request. EU consumer protection laws may also apply.
          </li>
          <li>
            <strong>California, USA</strong>: California residents may request a
            list of third parties receiving their personal data for marketing
            purposes by contacting us at{" "}
            <a
              href="mailto:support@getlinqx.com"
              className="text-primary hover:underline"
            >
              support@getlinqx.com
            </a>
            .
          </li>
          <li>
            <strong>Australia</strong>: Australian residents may request a copy
            of our Privacy Policy by emailing{" "}
            <a
              href="mailto:support@getlinqx.com"
              className="text-primary hover:underline"
            >
              support@getlinqx.com
            </a>
            .
          </li>
        </ul>
      </section>

      <section id="acknowledgment" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-foreground">
          Acknowledgment
        </h2>
        <p className="text-muted-foreground">
          By using the Services, you acknowledge that you have read, understood,
          and agree to be bound by this EULA, including any applicable
          jurisdiction-specific provisions.
        </p>
      </section>
    </LegalLayout>
  );
}
